import { Box, Link, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { modalText } from '~/pages/Payment/styles';

interface MarkdownContentProps {
  content: string;
}

const textColor = { color: 'rgba(255, 255, 255, 0.9)' };
const modalTextWithColor = { ...modalText, ...textColor };

const components = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <Typography variant="body1" sx={modalTextWithColor} paragraph>
      {children}
    </Typography>
  ),
  h1: ({ children }: { children?: React.ReactNode }) => (
    <Typography
      variant="h5"
      sx={{ fontWeight: 600, marginBottom: 1.5, marginTop: 2, ...textColor, color: 'primary.main' }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <Typography
      variant="h6"
      sx={{ fontWeight: 600, marginBottom: 1.5, marginTop: 2, ...textColor, color: 'primary.main' }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: 1, marginTop: 1.5, ...textColor }}>
      {children}
    </Typography>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <Typography component="strong" sx={{ fontWeight: 700, ...textColor }}>
      {children}
    </Typography>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" color="primary">
      {children}
    </Link>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <Box component="ul" sx={{ pl: 2.5, mb: 1 }}>
      {children}
    </Box>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <Box component="ol" sx={{ pl: 2.5, mb: 1 }}>
      {children}
    </Box>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <Typography component="li" variant="body1" sx={{ ...modalTextWithColor, display: 'list-item', mb: 0.5 }}>
      {children}
    </Typography>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <Box
      component="blockquote"
      sx={{ borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, my: 1.5, ...textColor }}
    >
      <Typography variant="body1" sx={modalTextWithColor}>
        {children}
      </Typography>
    </Box>
  )
} as const;

export default function MarkdownContent({ content }: MarkdownContentProps) {
  if (!content?.trim()) return null;
  return (
    <Box sx={textColor}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </Box>
  );
}
